/**
 * @component LaydownLogFormComponent
 *
 * Manages the laydown space log form, enabling users to create new laydown log entries for storage or asset tracking.
 * This component provides a streamlined form experience, focusing on essential fields only: job number, contact, space,
 * storage facility, and relevant dates (date in, estimated date out, date out). The form is initialized with defaults such as
 * the current user for the "loggedBy" field and the currently selected storage facility.
 *
 * Key Features:
 * - Responsive UI: Adjusts layout and field presentation based on screen size using screen size service subscriptions.
 * - Dynamic Defaults: Automatically populates fields like storage facility and "loggedBy" based on user and location context.
 * - Location Restrictions: Conditionally renders based on whether the selected location supports laydown space.
 *   Shows LaydownLocationGuardComponent with helpful message when location doesn't support laydown.
 * - Contact Autocomplete: Contact field includes employee email autocomplete dropdown for quick selection.
 * - Minimalist Data Model: Does not include advanced features such as PO validation, photo management, print label generation,
 *   or email notifications. These features are specific to the Receiving Log Form.
 * - UI Configuration: Loads UI text, label placement, and toast messages from a central configuration service for consistency.
 *
 * Subscriptions:
 * - Listens for changes in screen size, user info, selected location, and data loading state to keep the form contextually accurate.
 * - Location changes update the storage facility field and trigger conditional rendering based on laydown availability.
 * - Cleans up all subscriptions on destroy to prevent memory leaks.
 *
 * Notes:
 * - The form is built dynamically from configuration data, allowing for future extensibility.
 * - All form submissions are validated for required fields; on success, the form is reset and a success message is shown.
 * - The component is intentionally limited in scope to basic laydown log creation, delegating more complex workflows to other components.
 * - Consider refactoring form initialization and subscription management for even greater maintainability as requirements grow.
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { LaydownLogService } from '../../../service/laydown-log.service';
import { AssetsDataService } from '../../../service/assets-data.service';
import { ToastService } from '../../../service/toast.service';
import { ScreenSizeService } from '../../../service/screen-size.service';
import { SelectedLocationService } from '../../../service/selected-location.service';
import { ContactsService } from '../../../service/contact.service';
import { EntraService } from '../../../service/entra.service';
import {
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { LaydownLogRow } from '../../../models/logs/laydown-space/LaydownLogRow';
import { ApiLaydownLogRow } from '../../../models/logs/laydown-space/ApiLaydownLogRow';
import { DataLoading } from '../../../models/DataLoading.interface';
import {
  LoadingText,
  ToastText,
} from '../../../models/assets-data/UiConfig.interface';
import { SelectedLocation } from '../../../models/selectedLocation.interface';
import { User } from '../../../models/User.interface';
import {
  LaydownFormInput,
  Option,
} from '../../../models/assets-data/laydown/LaydownLogFormConfig.interface';

@Component({
  selector: 'app-laydown-log-form',
  templateUrl: './laydown-log-form.component.html',
  styleUrls: ['./laydown-log-form.component.css'],
})
export class LaydownLogFormComponent implements OnInit, OnDestroy {
  //---------------------- Services ---------------------------------------------//

  private _toastService: ToastService; // Service for toast notifications

  private _entraService: EntraService; // Service for authentication-related tasks
  private _screenSizeService: ScreenSizeService; // Service for managing screen size detection
  private _assetsDataService: AssetsDataService; // Service for asset data management
  private _selectedLocationService: SelectedLocationService; // Service for managing selected location
  private _laydownLogService: LaydownLogService; // Service for laydown log operations
  private _contactsService: ContactsService; // Service for managing contacts

  //---------------------- Subscriptions ---------------------------------------------//

  
  // Subscriptions for data streams from services that fetch and update data through the backend API
  public employeeEmailsSubscription!: Subscription; // Subscription for company emails
  public isLoadingContactsSubscription!: Subscription; // Subscription for contact loading state
  public photosSubscription!: Subscription; // Subscription for photo data updates
  public poDataSubscription!: Subscription; // Subscription for purchase order data updates
  public dataLoadingSubscription!: Subscription; // Subscription for data loading status updates
  //user-controlled data streams
  public locationSubscription!: Subscription; // Subscription for location data changes

  // Subscriptions for screen size and user info changes
  public isLargeScreenSubscription!: Subscription; // Subscription for large screen detection
  public isSmallScreenSubscription!: Subscription; // Subscription for small screen detection
  public userSubscription!: Subscription; // Subscription for username changes

  // Subscriptions for form field changes
  public jobNumberSubscription!: Subscription; // Subscription for job number changes
  public spaceSubscription!: Subscription; // Subscription for space changes

  //---------------------- General Data and State Management---------------------------------------------//

  public selectedLocation!: SelectedLocation;
  public employeeEmails: string[] = []; // List of company emails for contact selection
  public isLoadingContacts: boolean = false; // Loading state for contact emails

  public dataLoading: DataLoading = { isLoading: false, message: '' }; // Data loading state and message

  // Flags to determine screen size for responsive design
  public isLargeScreen!: boolean; // Boolean indicating if the screen is large
  public isSmallScreen!: boolean; // Boolean indicating if the screen is small

  // Placeholder username for the component
  public username: string = 'Test User'; // Default username value
  public userEmail: string = ''; // Default username value
  public assetsDataLoaded = false; // Boolean flag for asset data loading status

  //----------- UI Text configurations ---------------------
  public toastText!: ToastText;
  public loadingText!: LoadingText; // Text for loading state

  //-----------Form and Input Management  ---------------------

  // Form Inputs and Fields:
  public logFormInputs!: Record<string, LaydownFormInput>; // Form inputs for the log form
  public submitButtonText!: string; // Text for the submit button

  // Form groups
  public logForm!: FormGroup; // Form group for the log form

  //-----------Form Configuration and Settings ---------------------

  // Form Defaults and Validation:

  public labelPlacement!: string; // Label placement settings for printing

  //Notification and Contact Management

  public shelfLocation!: string; // Shelf location info for the Laydown log

  //----------- Form State Management ---------------------
  public spaceOptions: Option[] = []; // Options for the space select input

  // Initializes the component, injects services, and loads initial data
  constructor(
    laydownLogService: LaydownLogService,
    assetsDataService: AssetsDataService,
    toastService: ToastService,
    screenSizeService: ScreenSizeService,
    selectedLocationService: SelectedLocationService,
    entraService: EntraService,
    contactsService: ContactsService

  ) {
    this._toastService = toastService;

    this._assetsDataService = assetsDataService;

    this._screenSizeService = screenSizeService;
    this._entraService = entraService;
    this._laydownLogService = laydownLogService;
    this._selectedLocationService = selectedLocationService;
    this._contactsService = contactsService;

    this.isLargeScreen = this._screenSizeService.isLargeScreen.value;
    this.isSmallScreen = this._screenSizeService.isSmallScreen.value;

    // Fetch and set the company emails if they haven't been fetched yet
    this.selectedLocation = this._selectedLocationService.getSelectedLocation();
    
    // Fetch initial logs for the current location, which will set occupied spaces
    this._laydownLogService.fetchLaydownLogs(undefined, this.selectedLocation.name);
    
    // Load UI config
    this.getUiConfig();

      
    // Company emails are fetched once per app session (warmed at login) — this
    // is a cache read unless the app-load prefetch has not landed yet.
    // `employeeEmails` is kept current by the fetchedCompanyEmails subscription in
    // setSubscriptions() — this only makes sure the list has been requested.
    // The service toasts the failure itself; this handler just keeps a failed
    // fetch from surfacing as an unhandled rejection.
    this._contactsService.ensureCompanyEmails().subscribe({
      error: (err) => console.error('Employee directory unavailable:', err),
    });

    // Load form data and BUILD the form immediately
    this.initializeForm();
  }

  // Initializes component state, sets up subscriptions
  ngOnInit() {
    this.setSubscriptions();
  }

  // Cleans up subscriptions and resets form when the component is destroyed
  ngOnDestroy() {
    if (this.employeeEmailsSubscription){
      this.employeeEmailsSubscription.unsubscribe();
    }
    
    if (this.isLoadingContactsSubscription){
      this.isLoadingContactsSubscription.unsubscribe();
    }
        
    if (this.dataLoadingSubscription){
  this.dataLoadingSubscription.unsubscribe();
    }

    if (this.locationSubscription) 
      {
        this.locationSubscription.unsubscribe();
      }
    if (this.isLargeScreenSubscription)
    {
      this.isLargeScreenSubscription.unsubscribe();
    }
    if (this.isSmallScreenSubscription)
    {
      this.isSmallScreenSubscription.unsubscribe();
    }
    if (this.userSubscription) 
    {
      this.userSubscription.unsubscribe();
    }

    if (this.jobNumberSubscription) {
      this.jobNumberSubscription.unsubscribe();
    }

    if (this.spaceSubscription) {
      this.spaceSubscription.unsubscribe();
    }

    if (this.logForm) {
      this.logForm.reset();
    }
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------//
  // Methods for component initialization

  // Loads and applies UI configuration (label placement, modals, toast messages)
  public getUiConfig(): void {
    const uiConfigData = this._assetsDataService.GetUiConfigData();
    this.labelPlacement = uiConfigData.labelPlacement.logForm;
    this.toastText = uiConfigData.toastText;
    this.loadingText = uiConfigData.loadingText;
  }

  // Subscribes to multiple data streams from various services and updates component properties accordingly.
  public setSubscriptions(): void {
    // Unsubscribe from existing subscriptions to prevent memory leaks
    this.unsubscribeAll();


        // Subscribe to fetched company emails and update `employeeEmails` property.
    //This will be used to populate the drop down of company contacts in the child add-contact component
    this.employeeEmailsSubscription =
      this._contactsService.fetchedCompanyEmails.subscribe(
        (contacts: string[]) => {
          this.employeeEmails = contacts;
        }
      );

    // Subscribe to contact loading state
    this.isLoadingContactsSubscription =
      this._contactsService.isLoadingEmails.subscribe(
        (isLoading: boolean) => {
          this.isLoadingContacts = isLoading;
        }
      );

    // Subscribe to job number field changes to validate space occupancy
    this.jobNumberSubscription = this.logForm.get('jobNumber')!.valueChanges.subscribe(
      () => {
        this.validateSpaceOccupancy();
      }
    );

    // Subscribe to space field changes to validate space occupancy
    this.spaceSubscription = this.logForm.get('space')!.valueChanges.subscribe(
      () => {
        this.validateSpaceOccupancy();
      }
    );

    // Subscribe to contact field changes to validate selected value
    this.logForm.get('contact')!.valueChanges.subscribe(
      (value: string) => {
        this.validateContactSelection(value);
      }
    );

    // Subscribe to large screen size changes and update `isLargeScreen` property.
    //This will be used to adjust the form layout based on screen size
    this.isLargeScreenSubscription =
      this._screenSizeService.isLargeScreen.subscribe(
        (isLargeScreen: boolean) => {
          this.isLargeScreen = isLargeScreen;
        }
      );

    // Subscribe to small screen size changes and update `isSmallScreen` property.
    //This will be used to adjust the form layout based on screen size
    this.isSmallScreenSubscription =
      this._screenSizeService.isSmallScreen.subscribe(
        (isSmallScreen: boolean) => {
          this.isSmallScreen = isSmallScreen;
        }
      );

    // Subscribe to username changes and update `username` property.
    //This will be used to display the logged-in user's name on any form submissions
    this.userSubscription = this._entraService.user.subscribe((user: User) => {
      this.userEmail = user.email;
      this.username = user.name;
    });

    // Subscribe to selected location changes and update `selectedLocation` property.
    // This will be used to populate the storage facility field and fetch logs for the new location
    this.locationSubscription =
      this._selectedLocationService.selectedLocation$.subscribe(
        (location: SelectedLocation) => {
          this.selectedLocation = location;
          if (this.logForm && this.logForm.controls['storageFacility']) {
            this.logForm.controls['storageFacility'].setValue(location.label);
          }
          
          // Fetch logs for the new location (service will extract occupied spaces automatically)
          this._laydownLogService.fetchLaydownLogs(undefined, location.name);
        }
      );

    // Subscribe to data loading status and update `dataLoading` property.
    //This will be used to display loading indicators and messages during data fetches
    this.dataLoadingSubscription =
      this._laydownLogService.dataLoading.subscribe(
        (dataLoading: DataLoading) => {
          this.dataLoading = dataLoading;
        }
      );
  }

  //Unsubscribes from all subscriptions to prevent memory leaks
  public unsubscribeAll(): void {
 if (this.employeeEmailsSubscription){
      this.employeeEmailsSubscription.unsubscribe();
    }
    
    if (this.isLoadingContactsSubscription){
      this.isLoadingContactsSubscription.unsubscribe();
    }
        
    if (this.dataLoadingSubscription){
  this.dataLoadingSubscription.unsubscribe();
    }

    if (this.locationSubscription) 
      {
        this.locationSubscription.unsubscribe();
      }
    if (this.isLargeScreenSubscription)
    {
      this.isLargeScreenSubscription.unsubscribe();
    }
    if (this.isSmallScreenSubscription)
    {
      this.isSmallScreenSubscription.unsubscribe();
    }
    if (this.userSubscription) 
    {
      this.userSubscription.unsubscribe();
    }
    if (this.jobNumberSubscription) {
      this.jobNumberSubscription.unsubscribe();
    }
    if (this.spaceSubscription) {
      this.spaceSubscription.unsubscribe();
    }
  }

  private initializeForm(): void {
    const logFormData = this._assetsDataService.GetLaydownLogFormData();

    if (!logFormData) {
      console.error('Failed to load form data');
      return;
    }

    // Store form inputs
    this.logFormInputs = logFormData.inputs;
    this.submitButtonText = logFormData.submitButtonText;

    // Extract space options
    const spaceOptions = this.logFormInputs['space']?.['options'];
    if (spaceOptions) this.spaceOptions = spaceOptions;

    // BUILD THE FORM HERE - this happens in constructor, before template renders
    this.logForm = new FormBuilder().group({
      jobNumber: [
        '',
        this.logFormInputs['jobNumber']?.required ? [Validators.required] : [],
      ],
      contact: [
        undefined,
        this.logFormInputs['contact']?.required ? [Validators.required, Validators.email] : [],
      ],
      space: [
        '',
        this.logFormInputs['space']?.required ? [Validators.required] : [],
      ],
      storageFacility: [{ value: this.selectedLocation.label, disabled: true }],
      dateIn: [
        new Date().toISOString(),
        this.logFormInputs['dateIn']?.required ? [Validators.required] : [],
      ],
      estDateOut: [
        '',
        this.logFormInputs['estDateOut']?.required ? [Validators.required] : [],
      ],
    });

    // Mark as loaded AFTER form is created
    this.assetsDataLoaded = true;

  }

  /**
   * Handles form submission.
   * Validates the form and submits to the service if valid.
   */
  public onSubmitLog(): void {
    if (!this.logForm.valid) {
      this.logForm.markAllAsTouched();
      this._toastService.showWarningMessage(
        'Please fill out all required fields'
      );
      return;
    }

    const formData = this.logForm.value;

    // Prepare the log data for submission
    const newLaydownLogRow = this.prepareLogForSubmission(formData);

    // Submit to service
    this._laydownLogService.submitLog(newLaydownLogRow, () => {
      this.handleLogSuccess();
    });
  }

  /**
   * Prepares the form data for submission to the API.
   */
  private prepareLogForSubmission(formData: any): ApiLaydownLogRow {
    // Convert form data to LaydownLogRow format
    const laydownLogRow = new LaydownLogRow({
      jobNumber: formData.jobNumber,
      contact: formData.contact,
      space: formData.space,
      dateIn: formData.dateIn,
      estDateOut: formData.estDateOut,
      dateOut: formData.dateOut,
      storageFacility: this.selectedLocation.label,
      loggedBy: this.username,
    });

    // Convert dates to friendly format if needed
    this._laydownLogService.setDateFriendlyLogRowData(laydownLogRow);

    // Convert to API format
    const apiLaydownLogRow = new ApiLaydownLogRow(laydownLogRow);

    return apiLaydownLogRow;
  }

  /**
   * Handles successful form submission.
   * Resets the form and shows success message.
   */
  private handleLogSuccess(): void {
    this.resetForm();
  }

  /**
   * Validates that the contact field contains a value from the employee emails list.
   * If the value is not in the list, marks the field as invalid.
   */
  private validateContactSelection(value: string): void {
    if (!value || value.trim() === '') {
      return;
    }

    // Check if the value exists in the employee emails list
    if (this.employeeEmails.length > 0 && !this.employeeEmails.includes(value)) {
      this.logForm.get('contact')?.setErrors({ notSelected: true });
    } else if (this.logForm.get('contact')?.hasError('notSelected')) {
      // Clear the notSelected error if the value is now valid
      const contactControl = this.logForm.get('contact');
      const errors = { ...contactControl?.errors };
      delete errors['notSelected'];
      contactControl?.setErrors(Object.keys(errors).length > 0 ? errors : null);
    }
  }

  /**
   * Validates that the job number and space combination is not already occupied.
   * Checks with the service if the space is already occupied by this job number.
   * If occupied, shows a warning toast and marks the space field as invalid.
   */
  private validateSpaceOccupancy(): void {
    const jobNumber = this.logForm.get('jobNumber')?.value;
    const space = this.logForm.get('space')?.value;

    // Only validate if both fields have values
    if (!jobNumber || !space) {
      return;
    }

    // Check with service if this space is occupied by this job number
    if (this._laydownLogService.isSpaceOccupiedByJob(jobNumber, space)) {
      this._toastService.showWarningMessage(
        `Job ${jobNumber} is already occupying Space ${space}. Please select a different space or job number.`
      );
      
      // Mark job number field as invalid
      this.logForm.get('jobNumber')?.setErrors({ occupied: true });
    } else {
      // Clear the occupied error if it was previously set
      const jobNumberControl = this.logForm.get('jobNumber');
      if (jobNumberControl?.hasError('occupied')) {
        jobNumberControl.setErrors(null);
      }
    }
  }

  /**
   * Resets the form to its initial state.
   * Keeps dateIn set to today's date.
   */
  public resetForm(): void {
    this.logForm.reset();
    this.logForm.controls['dateIn'].setValue(new Date().toISOString());
  }
}
